import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { AdMob, InterstitialAdPluginEvents } from "@capacitor-community/admob";
import { useT } from "../i18n.ts";

/**
 * A full-screen ad placeholder over the canvas — used at several placements
 * (Game.tsx's between-act adGate, MatchScreen.tsx's idle-timeout nudge) that
 * each have their own AdMob interstitial unit (see gamzone/ads/config.ts).
 * No real ad network is wired for the reward stand-in (RewardedAd.tsx uses a
 * rewarded unit instead, not this component), so this always shows the
 * "광고 영역" placeholder there; `onContinue` fires whatever dismissing the ad
 * should do (proceeding to the next 막, closing back to the result popup, …).
 *
 * `real` + `adUnitId` opt a call site into a genuine AdMob interstitial for
 * that specific placement — pass both, or neither (stays a placeholder).
 */
export function Interstitial({
  onContinue,
  continueLabel,
  real = false,
  adUnitId,
}: {
  onContinue: () => void;
  continueLabel?: string;
  real?: boolean;
  /** The AdMob interstitial unit ID for this placement — required when `real` is set. */
  adUnitId?: string;
}) {
  const t = useT();
  const showReal = real && Capacitor.isNativePlatform() && Boolean(adUnitId);

  // onContinue is a fresh inline closure every render at every call site;
  // read it through a ref so the load/show effect below only reruns when
  // showReal itself changes, not on every parent re-render.
  const onContinueRef = useRef(onContinue);
  onContinueRef.current = onContinue;

  useEffect(() => {
    if (!showReal) return;
    let cancelled = false;
    const dismissedSub = AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
      if (!cancelled) onContinueRef.current();
    });
    const failSub = AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => {
      if (!cancelled) onContinueRef.current();
    });
    (async () => {
      try {
        await AdMob.prepareInterstitial({ adId: adUnitId! });
        if (cancelled) return;
        await AdMob.showInterstitial();
      } catch {
        // couldn't load/show — don't block on an ad failure
        if (!cancelled) onContinueRef.current();
      }
    })();
    return () => {
      cancelled = true;
      dismissedSub.then((h) => h.remove()).catch(() => {});
      failSub.then((h) => h.remove()).catch(() => {});
    };
  }, [showReal, adUnitId]);

  if (showReal) return null; // real ad is a native full-screen overlay
  return (
    <div className="gz-interstitial">
      <div className="gz-interstitial-box">
        <span>{t("adArea")}</span>
      </div>
      <button
        type="button"
        className="gz-interstitial-close"
        onClick={onContinue}
      >
        {continueLabel ?? t("close")}
      </button>
    </div>
  );
}
